import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { io, type Socket } from 'socket.io-client'
import { useToast } from 'vue-toast-notification'
import router from '@/router'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useUserNotificationStore } from '@/stores/useUserNotificationStore'
import { useNewsStore } from '@/stores/useNewsStore'
import { NEWS_TYPE_LABELS } from '@/utils/newsDisplay'
import type { NewsType } from '@/stores/useNewsAdminStore'

interface NewNotificationPayload {
  uuid: string
  title: string
  description: string
  type: string
  entityType: string
  entityId: string
  createdAt: string
}

interface NewsPublishedPayload {
  uuid: string
  type: NewsType
  title: string
  publishedAt: string
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Dedicated WebSocket client for the boostcore /notifications namespace.
 *
 * Lifecycle is driven by boostcore identity: when `boostCoreStore.user` is set
 * we open the socket; when it's cleared we close it. Auth happens server-side
 * via the `core_session` cookie sent in the upgrade handshake — no payload to
 * push, no Azure token coupling.
 *
 * Listens to `unread-count` events and pushes them straight into
 * useUserNotificationStore.unreadCount.
 */
export const useNotificationWsStore = defineStore('notificationWs', () => {
  const socket = ref<Socket | null>(null)
  const isConnected = ref(false)

  const userUuid = computed(() => useBoostCorePermissionStore().user?.uuid ?? null)

  function buildUrl(): string {
    const base = (import.meta.env.VITE_SUITE_URL as string) || ''
    return base.replace(/\/$/, '') + '/notifications'
  }

  function connect() {
    if (socket.value && (socket.value.connected || socket.value.active)) return
    if (socket.value) socket.value.disconnect()

    const url = buildUrl()
    const s = io(url, {
      withCredentials: true,
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1_000,
      reconnectionDelayMax: 10_000,
    })

    s.on('connect', () => {
      isConnected.value = true
    })

    s.on('disconnect', () => {
      isConnected.value = false
    })

    s.on('unread-count', (payload: { count?: number }) => {
      if (typeof payload?.count === 'number') {
        useUserNotificationStore().unreadCount = payload.count
      }
    })

    s.on('notification:new', (payload: NewNotificationPayload) => {
      if (!payload?.title) return
      const safeTitle = escapeHtml(payload.title)
      // Negative top/bottom margins on an outer wrapper visually trim the
      // toast's built-in padding without needing a custom className (the lib
      // doesn't expose one — its only props are message/type/position/duration/
      // dismissible/onDismiss/onClick/queue/pauseOnHover).
      const html =
        '<div style="margin:-4px 0;">' +
          '<div style="font-size:9px;text-transform:uppercase;letter-spacing:0.06em;opacity:0.7;line-height:1;">Nouvelle notification</div>' +
          `<div style="font-weight:700;line-height:1.2;margin-top:1px;">${safeTitle}</div>` +
          '<div style="font-size:0.78em;opacity:0.8;margin-top:2px;">Cliquez pour plus de détails</div>' +
        '</div>'
      useToast().info(html, {
        duration: 8000,
        position: 'top-right',
        onClick: () => router.push('/notifications'),
      })
    })

    s.on('news:published', (payload: NewsPublishedPayload) => {
      if (!payload?.title) return
      useNewsStore().onNewsPublished()
      const kicker = NEWS_TYPE_LABELS[payload.type] ?? 'Actualité'
      const safeTitle = escapeHtml(payload.title)
      const html =
        '<div style="margin:-4px 0;">' +
          `<div style="font-size:9px;text-transform:uppercase;letter-spacing:0.06em;opacity:0.7;line-height:1;">${escapeHtml(kicker)}</div>` +
          `<div style="font-weight:700;line-height:1.2;margin-top:1px;">${safeTitle}</div>` +
          '<div style="font-size:0.78em;opacity:0.8;margin-top:2px;">Cliquez pour ouvrir le fil d&#39;actualité</div>' +
        '</div>'
      useToast().info(html, {
        duration: 8000,
        position: 'top-right',
        onClick: () => router.push('/news'),
      })
    })

    s.on('connect_error', (err) => {
      isConnected.value = false
      // Silent: the store ref is enough to drive fallback polling.
      console.debug('notificationWs: connect_error', err?.message)
    })

    socket.value = s
  }

  function disconnect() {
    if (socket.value) {
      socket.value.disconnect()
      socket.value = null
    }
    isConnected.value = false
  }

  // Open/close the socket and toggle the notification polling fallback
  // alongside the boostcore identity. When the WS is connected, the server
  // pushes counts; when it isn't, polling takes over.
  watch(
    userUuid,
    (uuid) => {
      const notif = useUserNotificationStore()
      if (uuid) {
        connect()
        // Optimistic: start polling immediately as a fallback. The
        // isConnected watcher below will stop it as soon as the WS attaches.
        notif.startUnreadCountPolling()
      } else {
        disconnect()
        notif.stopUnreadCountPolling()
      }
    },
    { immediate: true },
  )

  watch(isConnected, (connected) => {
    const notif = useUserNotificationStore()
    if (connected) {
      // WS took over — drop the polling timer, refresh once to be in sync.
      notif.stopUnreadCountPolling()
      notif.fetchUnreadCount()
    } else if (userUuid.value) {
      // WS dropped while authenticated — fall back to polling.
      notif.startUnreadCountPolling()
    }
  })

  return {
    socket,
    isConnected,
    connect,
    disconnect,
  }
})
